import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import CharacterLike from '~/components/CharacterDetails/CharacterLike/CharacterLike';
import CommentContainer from '~/components/Comment/CommentContainer';
import { Icon } from '~/components/atomic';
import Button from '~/components/atomic/Button/Default/DefaultButton';
import { HelmetHead } from '~/components/common';
import CharacterLiked from '~/components/common/CharacterCard/CharacterLiked/CharacterLiked';
import CreatorTag from '~/components/common/CharacterCard/CreatorTag/CreatorTag';
import { Header } from '~/components/common/Header';
import { useWebSocket } from '~/hooks';
import useGameLikeAction from '~/hooks/useGameLikeAction';
import { GameDetailData, MessageResponse } from '~/types';
import { PAGE_ROUTE } from '~/constants';
import { breakpoint, fontStyle } from '~/styles/themes';
import { useUserStore } from '~/store';

export default function GameDetailPage({
  gameDetails,
}: {
  gameDetails: GameDetailData;
}) {
  const navigate = useNavigate();

  const email = useUserStore((state) => state.email);
  const gameId = String(gameDetails.game_id);

  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [socketConnectSuccess, setSocketConnectSuccess] = useState(false);
  const [isGameLiked, setIsGameLiked] = useState<boolean>(gameDetails.is_liked);

  const { handleLikeClick } = useGameLikeAction();

  useWebSocket({
    chatType: 'mission',
    fetchDone: true,
    email,
    setMessages,
    socketConnectSuccess,
    setSocketConnectSuccess,
  });
  return (
    <>
      <HelmetHead title='Mission Game' />
      <Header.Root
        style={{ padding: '0 8px 0 4px', backgroundColor: 'transparent' }}
        transparentBg
      >
        <Header.PrevButton onPrevClick={() => navigate(PAGE_ROUTE.MAIN)} />
        <Header.Section>
          <Header.Dart transparentBg={false} />
        </Header.Section>
      </Header.Root>

      <Container>
        <TopGameInfoSection>
          <ImageBackground
            src={gameDetails?.thumbnail_file_path}
            alt='game background'
          />

          <GameInfoContainer>
            <CreatorTag create_username={gameDetails.create_username} />
            <Title>{gameDetails.title}</Title>
            <Intro>{gameDetails.introduce}</Intro>
            {/* 캐릭터 하트(찜하기) 컴포넌트 */}
            <GameLikeContainer>
              <CharacterLiked
                id={gameId}
                is_liked={isGameLiked}
                handleLikeClick={() => {
                  handleLikeClick(String(gameId), isGameLiked);
                  setIsGameLiked((prev) => !prev);
                }}
              />
            </GameLikeContainer>
          </GameInfoContainer>
        </TopGameInfoSection>
        <BottomControlSection>
          <EmojiAndCommentContainer>
            {/* 이모지 공감 컴포넌트 */}
            <CharacterLike
              character_id={String(gameId)}
              emojis={gameDetails.emojis.filter((emoji) => emoji.count !== 0)}
              type={'mission'}
            />
            <CommentContainer
              viewComment={gameDetails.comments}
              isMissionGame
            />
          </EmojiAndCommentContainer>
          <ButtonBox>
            <Button onClick={() => navigate(`/mission/main/${String(gameId)}`)}>
              <Icon iconName='Play' color='hierarchy1_reverse' />
              <h5>Play</h5>
            </Button>
          </ButtonBox>
        </BottomControlSection>
      </Container>
    </>
  );
}

const TopGameInfoSection = styled.div`
  position: relative;
  width: 100%;
  height: 480px;
`;

const GameInfoContainer = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  flex-direction: column;
  justify-content: end;
  width: 100%;
  height: 100%;
  padding: 0 16px;
  background: linear-gradient(180deg, #ff000000 35%, #000000);
  gap: 12px;
`;

export const ImageBackground = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding-bottom: 1px;
`;

const BottomControlSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100% - 480px);
  min-height: 250px;
  padding: 20px 16px 20px;
  overflow: auto;
  background-color: black;
`;
export const Container = styled.div`
  width: 100%;
  max-width: ${breakpoint.mobile_large};
  height: 100%;
`;

const Title = styled.div`
  ${({ theme }) => css`
    color: ${theme.content.content_light_1};
    ${fontStyle.text.body18_medium}
  `}
`;

const Intro = styled.div`
  font-size: 14px;
  ${({ theme }) => css`
    color: ${theme.content.content_light_3};
  `}
`;

const EmojiAndCommentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GameLikeContainer = styled.div`
  margin-top: -5px;
  margin-bottom: -15px;
  margin-left: -10px;
`;
export const ButtonBox = styled.div`
  ${({ theme }) => css`
    display: block;
    position: fixed;
    bottom: 0;
    left: 50%;
    width: 100%;
    max-width: ${breakpoint.mobile_large};
    padding: 0 ${theme.layout.section_side} 20px;
    transform: translateX(-50%);
  `}
`;
